import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  LogOut,
  User as UserIcon,
  ReceiptText,
  ChevronRight,
  MapPin,
  CreditCard,
  Settings as SettingsIcon,
  Trash2,
  Phone,
  Mail,
} from 'lucide-react';

import { useAuthStore } from '@/store/auth.store';
import { getClientMe, deleteProfile, listMyOrders } from '@/services/client';
import { formatPhone } from '@/utils/format';

export default function Profile() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.accessToken);
  const logout = useAuthStore((s) => s.logout);
  const setUser = useAuthStore((s) => s.setUser);

  const { data: me } = useQuery({
    queryKey: ['client/me'],
    queryFn: getClientMe,
    enabled: !!token,
  });

  const { data: orders } = useQuery({
    queryKey: ['client/orders'],
    queryFn: listMyOrders,
    enabled: !!token,
    refetchInterval: 30_000,
  });

  // Синхронизируем store с ответом бэка (имя могли обновить на другом устройстве).
  useEffect(() => {
    if (me?.user) {
      setUser({
        id: me.user.id,
        name: me.user.name ?? null,
        phone: me.user.phone ?? null,
        email: me.user.email ?? null,
      });
    }
  }, [me, setUser]);

  const delMut = useMutation({
    mutationFn: deleteProfile,
    onSuccess: () => {
      logout();
      navigate('/');
      toast('Аккаунт удалён');
    },
  });

  // Гость — компактный экран входа.
  if (!token || !me?.user) {
    return (
      <div className="pt-4">
        <div className="px-4 mb-4">
          <h1 className="text-2xl font-semibold">Профиль</h1>
        </div>
        <div className="px-4">
          <div className="card p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-brand-100 text-brand-700 mx-auto flex items-center justify-center mb-3">
              <UserIcon size={28} />
            </div>
            <p className="text-sm text-slate-500 mb-3">
              Войдите, чтобы делать заказы и управлять профилем.
            </p>
            <Link className="btn-primary" to="/login">Войти</Link>
          </div>
        </div>
      </div>
    );
  }

  const user = me.user;
  const ordersCount = orders?.length ?? 0;
  const addressesCount = user.addresses?.length ?? 0;
  const cardsCount = user.cards?.length ?? 0;

  return (
    <div className="pt-4 pb-8">
      <div className="px-4 mb-4">
        <h1 className="text-2xl font-semibold">Профиль</h1>
      </div>

      <div className="px-4 space-y-3">
        {/* Шапка с аватаром + имя/телефон/email */}
        <div className="card p-4 flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0">
            <UserIcon size={26} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{user.name || 'Без имени'}</div>
            {user.phone && (
              <div className="text-sm text-slate-500 truncate">
                {formatPhone(user.phone)}
              </div>
            )}
            {user.email && (
              <div className="text-sm text-slate-500 truncate">{user.email}</div>
            )}
          </div>
        </div>

        {/* 1) Имя */}
        <SectionList>
          <Row
            to="/profile/name"
            icon={<UserIcon size={18} />}
            iconBg="bg-brand-50 text-brand-600"
            title="Имя"
            subtitle={user.name || 'Не указано'}
          />
          {/* 2) Номер телефона (read-only) */}
          {user.phone && (
            <RowStatic
              icon={<Phone size={18} />}
              iconBg="bg-emerald-50 text-emerald-600"
              title="Номер телефона"
              subtitle={formatPhone(user.phone)}
            />
          )}
          {/* Email (read-only) — показываем только если привязан */}
          {user.email && (
            <RowStatic
              icon={<Mail size={18} />}
              iconBg="bg-sky-50 text-sky-600"
              title="Email"
              subtitle={user.email}
            />
          )}
        </SectionList>

        {/* 3–5) Заказы / Адреса / Карты */}
        <SectionList>
          <Row
            to="/orders"
            icon={<ReceiptText size={18} />}
            iconBg="bg-brand-50 text-brand-600"
            title="Заказы"
            subtitle={`${ordersCount} ${pluralize(ordersCount, 'заказ', 'заказа', 'заказов')}`}
          />
          <Row
            to="/addresses"
            icon={<MapPin size={18} />}
            iconBg="bg-amber-50 text-amber-600"
            title="Мои адреса"
            subtitle={addressesCount ? `${addressesCount} сохранено` : 'Не добавлены'}
          />
          <Row
            to="/cards"
            icon={<CreditCard size={18} />}
            iconBg="bg-indigo-50 text-indigo-600"
            title="Мои карты"
            subtitle={cardsCount ? `${cardsCount} сохранено` : 'Не добавлены'}
          />
        </SectionList>

        {/* 6) Настройки */}
        <SectionList>
          <Row
            to="/settings"
            icon={<SettingsIcon size={18} />}
            iconBg="bg-slate-100 text-slate-600"
            title="Настройки"
          />
        </SectionList>

        {/* 7) Выйти */}
        <button
          className="btn-outline w-full"
          onClick={() => {
            logout();
            navigate('/');
          }}
        >
          <LogOut size={16} /> Выйти
        </button>

        {/* 8) Удалить аккаунт */}
        <button
          className="btn-outline w-full text-red-600 border-red-200"
          onClick={() => {
            if (confirm('Удалить аккаунт? Данные будут очищены.')) delMut.mutate();
          }}
        >
          <Trash2 size={16} /> Удалить аккаунт
        </button>
      </div>
    </div>
  );
}

function SectionList({ children }: { children: React.ReactNode }) {
  return <div className="card divide-y divide-slate-100 overflow-hidden">{children}</div>;
}

function Row({
  to,
  icon,
  iconBg,
  title,
  subtitle,
}: {
  to: string;
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <Link to={to} className="p-4 flex items-center gap-3 active:bg-slate-50">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 truncate">{subtitle}</div>}
      </div>
      <ChevronRight size={18} className="text-slate-300 shrink-0" />
    </Link>
  );
}

function RowStatic({
  icon,
  iconBg,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 truncate">{subtitle}</div>}
      </div>
    </div>
  );
}

function pluralize(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}
